import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Sparkles, Eye, ShieldCheck, Zap, Check, X, Lock,
  Search, ClipboardList, Wand2, Rocket, LineChart, RotateCcw, MessageSquare,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { getGrowthWorklog } from "@/lib/api";

const LOOP = [
  { icon: Search, t: "Audit" }, { icon: ClipboardList, t: "Plan" }, { icon: Wand2, t: "Build" },
  { icon: Rocket, t: "Publish" }, { icon: LineChart, t: "Optimize" }, { icon: Sparkles, t: "Grow" },
];

const MODES = [
  { key: "recommend", icon: Eye, title: "Recommend Only", desc: "The agent prepares recommendations but changes nothing. You stay fully in control.", tone: "slate" },
  { key: "approval", icon: ShieldCheck, title: "Approval Required", desc: "The agent prepares changes and places them in an approval queue for your sign-off.", tone: "blue" },
  { key: "trusted", icon: Zap, title: "Trusted Automation", desc: "The agent automatically executes pre-approved, low-risk actions — and still asks before anything risky.", tone: "amber" },
];

const LOW_RISK = ["Fix broken internal links", "Update the XML sitemap", "Compress oversized images", "Repair missing metadata", "Flag outdated information", "Schedule previously approved content", "Send approved performance reports"];
const HIGH_RISK = ["Publish medical, financial or legal claims", "Remove pages or change URLs", "Create redirects", "Change prices or business hours", "Modify forms or payment flows", "Replace brand messaging", "Change integrations or customer data", "Large sitewide changes"];

const CAPABILITIES = [
  "Audit an existing website", "Import approved content & media", "Identify technical problems",
  "Find SEO & content opportunities", "Create an improvement plan", "Draft new content & pages",
  "Prepare local service-area pages", "Improve existing copy & CTAs", "Monitor forms & conversion paths",
  "Detect broken links & missing pages", "Prepare structured data", "Track leads & conversions",
];

const WORKLOG = [
  { time: "Today · 09:12", action: "Fixed 3 broken internal links", reason: "Links pointed to removed blog posts", pages: "/services, /about", who: "Growth Agent", mode: "Trusted Automation", status: "done", rollback: true, before: "3 links → 404", after: "3 links → live pages" },
  { time: "Today · 08:40", action: "Compressed 12 oversized images", reason: "Improve Core Web Vitals (LCP)", pages: "Homepage, /gallery", who: "Growth Agent", mode: "Trusted Automation", status: "done", rollback: true, before: "4.8 MB page weight", after: "1.6 MB page weight" },
  { time: "Yesterday · 16:20", action: "Drafted 'Emergency plumbing in Beaverton' page", reason: "High-intent local query with no matching page", pages: "New: /locations/beaverton", who: "Growth Agent", mode: "Approval Required", status: "pending", rollback: false, before: "No page", after: "Draft awaiting your approval" },
  { time: "Yesterday · 11:05", action: "Proposed new homepage headline", reason: "Clarity + conversion improvement", pages: "Homepage", who: "Growth Agent", mode: "Approval Required", status: "pending", rollback: false, before: "\"Welcome to our site\"", after: "\"Same-day plumbing you can count on\"" },
  { time: "Mon · 10:30", action: "Updated XML sitemap", reason: "3 new pages published last week", pages: "sitemap.xml", who: "Growth Agent", mode: "Trusted Automation", status: "done", rollback: true, before: "42 URLs", after: "45 URLs" },
];

const STATUS = {
  done: { cls: "bg-emerald-50 text-emerald-700", label: "Completed" },
  pending: { cls: "bg-amber-50 text-amber-700", label: "Awaiting approval" },
  approved: { cls: "bg-blue-50 text-brand", label: "Approved" },
};
const DEFAULT_STATUS = { cls: "bg-slate-100 text-slate-500", label: "Logged" };

export default function GrowthAgentPage() {
  const [mode, setMode] = useState("approval");
  const [worklog, setWorklog] = useState(WORKLOG);
  useEffect(() => {
    window.scrollTo(0, 0); document.title = "WebsiteTech Growth Agent";
    getGrowthWorklog().then((d) => { if (d?.items?.length) setWorklog(d.items); }).catch(() => {});
  }, []);

  return (
    <>
      <Navbar />
      <main data-testid="growth-agent-page" className="bg-slate-50">
        {/* Hero */}
        <section className="pt-28 pb-14 bg-white border-b border-slate-100 relative overflow-hidden">
          <div className="absolute -top-32 -right-24 h-96 w-96 rounded-full blur-3xl bg-blue-100/60" />
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-3xl">
              <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.18em] text-brand"><Sparkles size={13} /> Flagship · Managed</span>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 mt-4">The WebsiteTech <span className="text-brand">Growth Agent.</span></h1>
              <p className="text-slate-500 mt-4 text-lg">An AI-assisted, human-supervised system that audits, plans and continuously improves your website — <strong className="text-slate-700">only ever within the guardrails you set.</strong></p>
              <div className="flex flex-wrap gap-3 mt-7">
                <Link to="/audit" className="inline-flex items-center gap-2 font-semibold rounded-xl px-6 py-3.5 bg-brand text-white hover:-translate-y-0.5 transition-transform" data-testid="ga-hero-audit">Start with a free audit <ArrowRight size={16} /></Link>
                <Link to="/tools" className="inline-flex items-center gap-2 font-semibold rounded-xl px-6 py-3.5 btn-outline">See all tools &amp; plans</Link>
              </div>
            </motion.div>
            {/* Loop */}
            <div className="flex flex-wrap items-center gap-2 mt-12">
              {LOOP.map((l, i) => (
                <div key={l.t} className="flex items-center gap-2">
                  <div className="inline-flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-full px-4 py-2 text-sm font-semibold text-slate-700"><l.icon size={15} className="text-brand" /> {l.t}</div>
                  {i < LOOP.length - 1 && <ArrowRight size={14} className="text-slate-300" />}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Governance modes */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="ga-modes">
          <div className="max-w-2xl">
            <span className="text-xs font-bold uppercase tracking-[0.18em] text-brand">You're always in control</span>
            <h2 className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900 mt-3">Three governance modes</h2>
            <p className="text-slate-500 mt-2">The agent never silently makes major changes. Choose how much autonomy it has — and change it anytime.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-4 mt-8">
            {MODES.map((m) => {
              const active = mode === m.key;
              return (
                <button key={m.key} onClick={() => setMode(m.key)} data-testid={`ga-mode-${m.key}`}
                  className={`text-left rounded-2xl p-6 border-2 transition-all ${active ? "border-brand bg-blue-50/50 -translate-y-1" : "border-slate-200 bg-white hover:border-slate-300"}`}>
                  <div className={`h-11 w-11 rounded-xl grid place-items-center ${active ? "bg-brand text-white" : "bg-slate-100 text-slate-500"}`}><m.icon size={20} /></div>
                  <h3 className="font-heading font-bold text-slate-900 mt-4">{m.title}</h3>
                  <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">{m.desc}</p>
                  {active && <span className="inline-flex items-center gap-1 text-xs font-semibold text-brand mt-3"><Check size={13} /> Selected</span>}
                </button>
              );
            })}
          </div>

          {/* Risk split */}
          <div className="grid md:grid-cols-2 gap-4 mt-8">
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50/40 p-6" data-testid="ga-low-risk">
              <div className="flex items-center gap-2 text-emerald-700 font-bold"><Zap size={18} /> Low-risk — can be automated</div>
              <ul className="mt-4 space-y-2">
                {LOW_RISK.map((x) => <li key={x} className="flex items-start gap-2 text-sm text-slate-600"><Check size={15} className="text-emerald-600 mt-0.5 shrink-0" /> {x}</li>)}
              </ul>
            </div>
            <div className="rounded-2xl border border-amber-200 bg-amber-50/40 p-6" data-testid="ga-high-risk">
              <div className="flex items-center gap-2 text-amber-700 font-bold"><Lock size={18} /> High-risk — always needs approval</div>
              <ul className="mt-4 space-y-2">
                {HIGH_RISK.map((x) => <li key={x} className="flex items-start gap-2 text-sm text-slate-600"><X size={15} className="text-amber-600 mt-0.5 shrink-0" /> {x}</li>)}
              </ul>
            </div>
          </div>
        </section>

        {/* Capabilities */}
        <section className="py-16 bg-white border-y border-slate-100">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900">What the agent does for you</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-8">
              {CAPABILITIES.map((c) => (
                <div key={c} className="flex items-center gap-2.5 bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-700"><Check size={15} className="text-brand shrink-0" /> {c}</div>
              ))}
            </div>
          </div>
        </section>

        {/* Work log */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="ga-worklog">
          <div className="max-w-2xl">
            <span className="text-xs font-bold uppercase tracking-[0.18em] text-brand">Full transparency</span>
            <h2 className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900 mt-3">Every change, logged</h2>
            <p className="text-slate-500 mt-2">A sample work log. You can always see exactly what the agent did, why, and roll it back in one click.</p>
          </div>
          <div className="mt-8 space-y-3">
            {worklog.map((w, i) => {
              const st = STATUS[w.status] || DEFAULT_STATUS;
              return (
                <div key={i} className="bg-white border border-slate-200 rounded-xl card-shadow p-4" data-testid={`ga-log-${i}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <div className="font-semibold text-slate-900">{w.action}</div>
                      <div className="text-xs text-slate-400 mt-0.5">{w.time} · {w.who} · <span className="text-slate-500">{w.mode}</span></div>
                    </div>
                    <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${st.cls}`}>{st.label}</span>
                  </div>
                  <p className="text-sm text-slate-500 mt-2"><span className="text-slate-400">Reason:</span> {w.reason} · <span className="text-slate-400">Pages:</span> {w.pages}</p>
                  <div className="grid sm:grid-cols-2 gap-2 mt-3">
                    <div className="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2 text-xs"><span className="text-slate-400 uppercase tracking-wide text-[10px]">Before</span><div className="text-slate-700 mt-0.5">{w.before}</div></div>
                    <div className="rounded-lg bg-emerald-50/60 border border-emerald-100 px-3 py-2 text-xs"><span className="text-emerald-600 uppercase tracking-wide text-[10px]">After</span><div className="text-slate-700 mt-0.5">{w.after}</div></div>
                  </div>
                  {w.rollback && <button className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-brand"><RotateCcw size={13} /> Roll back this change</button>}
                </div>
              );
            })}
          </div>
        </section>

        {/* CTA */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="rounded-3xl p-10 md:p-14 text-center bg-brand text-white">
              <h2 className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight">Put your website to work — 24/7.</h2>
              <p className="mt-3 text-white/80 max-w-xl mx-auto">Start with a free 360° audit. If you like what you see, switch on the Growth Agent in the mode you're comfortable with.</p>
              <div className="flex flex-wrap justify-center gap-3 mt-7">
                <Link to="/audit" className="inline-flex items-center gap-2 font-semibold rounded-xl px-6 py-3.5 bg-white text-brand hover:-translate-y-0.5 transition-transform">Get my free audit <ArrowRight size={16} /></Link>
                <Link to="/ai-chatbot" className="inline-flex items-center gap-2 font-semibold rounded-xl px-6 py-3.5 border border-white/40 hover:bg-white/10 transition-colors"><MessageSquare size={16} /> Talk to WebsiteTech AI</Link>
              </div>
              <p className="text-xs text-white/60 mt-5 max-w-lg mx-auto">Human-supervised AI. We never promise guaranteed rankings, traffic, AI citations, leads or revenue.</p>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
